/**
 * ROD Review Dialog for Links action.
 * Uses shared BaseModal and review components for consistency with campaigns.
 * 
 * Flow:
 * 1. User writes review + selects rating
 * 2. Clicks "Copy & Save Review" - copies text, saves, enables links
 * 3. User clicks links to open external review sites in new tabs
 * 4. User manually closes dialog via X button when done
 */
import { useState } from 'react';
import { Formik, Form } from 'formik';
import * as yup from 'yup';

import { api, useAPIContext } from '../../api/context';
import { useFPContext } from '../../utils/fingerprint';
import toast from '../../utils/toast';
import review from '../../components/review';
import BaseModal from '../BaseModal';
import styles from './RODReviewLinks.module.scss';

const RODReviewLinks = ({ item, onClose, onSubmit }) => {
  const { token } = useAPIContext();
  const { fp } = useFPContext();

  const config = item.config || {};
  const product = item.product;
  const previousData = item.previousData || {};
  const reviewLinks = config.review_links || [];
  const prefix = config.prefix || '';

  const [submitLoading, setSubmitLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const submit = async (values) => {
    setSubmitLoading(true);

    // Track if clipboard copy succeeded
    let clipboardSuccess = false;

    try {
      // Try to copy to clipboard, but don't fail if it doesn't work
      try {
        await navigator.clipboard.writeText(values.text);
        clipboardSuccess = true;
      } catch (clipboardErr) {
        // Clipboard failed - will show warning after successful submission
      }

      const fpResult = await fp.get();

      const response = await api(token).v1.products.rodSubmit.post(
        { rod_id: item.rod_id },
        {
          data_json: JSON.stringify(values),
          fingerprint: fpResult.visitorId,
        }
      );

      setSubmitLoading(false);

      if (response.status === 201) {
        if (clipboardSuccess) {
          toast.show(toast.KIND.SUCCESS, 'Copied! Now paste on the review site.');
        } else {
          toast.show(toast.KIND.WARNING, 'Review saved! Copy your review text manually.');
        }
        setSubmitted(true);
        // Do NOT call onSubmit here - dialog stays open for user to click links
        // User will close manually via X button when done
      } else if (response.status === 400) {
        toast.show(toast.KIND.ERROR, response.body?.error || response.body);
      }
    } catch (err) {
      setSubmitLoading(false);
      toast.show(toast.KIND.WARNING, err.message || err);
    }
  };

  // Handle close - also notify parent to move to next item
  const handleClose = () => {
    if (submitted && onSubmit) {
      onSubmit(); // Notify parent that review was completed
    }
    onClose?.();
  };

  const loading = submitLoading;

  return (
    <BaseModal title="Review" onClose={handleClose}>
      <div className={styles.root}>
        {/* Product header */}
        <div className={styles.product}>
          {product.images?.[0] && (
            <img src={product.images[0].image} alt="" />
          )}
          <div>
            <span>{product.brand_name}</span>
            <strong>{product.name}</strong>
          </div>
        </div>

        <Formik
          initialValues={{
            stars: previousData.stars || 0,
            text: previousData.text || prefix,
          }}
          validationSchema={yup.object({
            stars: yup.number()
              .min(1, 'Rating is required')
              .max(5, 'Invalid rating'),
            text: yup.string()
              .required('Review text is required'),
          })}
          validateOnChange={false}
          onSubmit={(values) => {
            if (loading || submitted) return;
            submit(values);
          }}
        >
          {({ values }) => (
            <Form>
              <review.Stars
                className={styles.stars}
                loading={loading || submitted}
                count={5}
              />

              <review.Text
                className={styles.text}
                loading={loading || submitted}
                label={(
                  <>
                    Review
                    {config.prefix && (
                      <span>
                        (Disclosure text added - DO NOT REMOVE THIS TEXT)
                      </span>
                    )}
                  </>
                )}
                prefix={prefix}
                length={{
                  min: config.min_length || 0,
                  max: config.max_length || 0,
                }}
                nlp={{
                  include: config.nlp_include,
                  exclude: config.nlp_exclude,
                  exclude_default: config.nlp_exclude_default,
                }}
              />

              {/* Step 1: Copy */}
              <button
                type="submit"
                disabled={loading || submitted}
              >
                {loading ? 'Saving...' : submitted ? '✓ Copied' : '1. Copy & Save Review'}
              </button>

              {/* Step 2+: External links - styled as buttons like campaigns */}
              {reviewLinks.map((link, i) => (
                <a
                  key={i}
                  href={link.link || link.url}
                  target="_blank"
                  rel="noreferrer"
                  className={!submitted ? styles.disabled : ''}
                  onClick={(e) => !submitted && e.preventDefault()}
                >
                  {i + 2}. Paste it on {link.name || link.label || 'Review Site'}
                </a>
              ))}

              {/* Done button (after submission) */}
              {submitted && (
                <button
                  type="button"
                  className={styles.doneBtn}
                  onClick={handleClose}
                >
                  Done
                </button>
              )}
            </Form>
          )}
        </Formik>
      </div>
    </BaseModal>
  );
};

export default RODReviewLinks;
